import { IEntry, IEntryPartial } from '@/types/entry'
import { Maybe } from '@/types/general'
import { DBStore } from '@/data/db-store'
import StoreAPI from '@/util/api/StoreAPI'
import PStorage, { DATA_LAST_UPDATED, USER_ID } from '@/util/PStorage'
import { Stencil } from '@/store/editor/types'
import stencils from '@/store/stencils'
import PCrypto from '@/util/crypto/PCrypto'
import Entry from './entry-model'

/**
 * Entry model repository for fetching models
 * from the IndexedDB and API
 *
 * Usage:
 *      On initial page load, run `fetchPartials` to pull
 *      in update data from the API
 *
 *      At any time, you can fetch any entry by id using
 *      `fetch(id)`. This will first attempt to load from the
 *      IDB, then the API. If the entry loaded from the IDB
 *      is pending an update, it will be refreshed before
 *      being returned.
 *
 *      Call `save(entry)` on the repository or `entry.save()`
 *      on the entry itself to persist it to the IDB and then
 *      the API.
 */
class EntryRepository {
    /**
     * Fetch updated partial entries from the API
     *
     * On the first load of the application, DATA_LAST_UPDATED will
     * be undefined and it will load all partial entries.
     *
     * From then on, it will only load updates and will set
     * the 'pendingUpdate' property on the entries. When they are
     * fetched to be used in the application, they will be loaded
     * fully from the API.
     *
     * Pass `force` as true to fetch all partial entries regardless
     * of DATA_LAST_UPDATED
     */
    public async fetchPartials(force: boolean = false): Promise<Entry[]> {
        const partials = await StoreAPI.getPartials(
            force ? undefined : PStorage.get(DATA_LAST_UPDATED) || undefined
        )
        PStorage.set(DATA_LAST_UPDATED, (new Date()).toISOString())
        const entries = await Promise.all(partials.map(this.fromPartial.bind(this)))
        return entries.filter(entry => !entry.deleted())
    }

    /**
     * Fetch all entries from the IDB
     */
    public async fetchAll(): Promise<Entry[]> {
        return (await DBStore.entries
            .toArray() as Entry[]).filter(entry => !entry.deleted())
    }

    /**
     * Fetch all entries that are pending updates
     */
    public async fetchStale(): Promise<Entry[]> {
        return (await DBStore.entries
            .filter(entry => entry.pendingUpdate)
            .toArray() as Entry[]).filter(entry => !entry.deleted())
    }

    /**
     * Get all entries with no parents
     */
    public async fetchTopLevelEntries(): Promise<Entry[]> {
        return (await DBStore.entries
            .filter(entry => entry.parentId === null)
            .toArray() as Entry[]).filter(entry => !entry.deleted())
    }

    /**
     * Fetch the direct children of an entry
     */
    public async fetchChildren(entryId: string): Promise<Entry[]> {
        return (await DBStore.entries
            .filter(entry => entry.parentId === entryId)
            .toArray() as Entry[]).filter(entry => !entry.deleted())
    }

    /**
     * Update an entry with data that has already been
     * retrieved - to avoid another round request after
     * an entry has been updated
     *
     * Pending update will be set to false.
     */
    public async updateModelData(data: IEntry): Promise<Entry> {
        const entry = await this.fetchFromIDB(data.entryId!) || new Entry(data.entryId)
        entry.setData({
            ...data,
            pendingUpdate: false
        })
        return this.saveToIDB(entry)
    }

    /**
     * Create a new entry object from a partial
     * from the API.
     *
     * Returns a pre-existing entry if it already exists
     */
    public async fromPartial(partial: IEntryPartial): Promise<Entry> {
        // attempt to fetch from IDB first so existing
        // data on the entry isn't overwritten
        let entry = await this.fetchFromIDB(partial.entryId)
        if (!entry) {
            // if it's a new entry, create it and
            // set dummy data
            // it will be updated when the user/worker
            // fetches it
            entry = new Entry(partial.entryId)
            entry.setData({
                ...partial,
                viewedAt: new Date(partial.viewedAt),
                updatedAt: new Date(partial.updatedAt),
                deletedAt: partial.deletedAt ? new Date(partial.deletedAt) : undefined,
                // unknowns
                tags: [],
                documentKey: '',
                content: '',
                createdBy: '',
                createdAt: new Date(),
                permission: 'read',
                pendingUpdate: true
            } as IEntry)
        } else {
            // if the entry was in the partial request,
            // it means it's been updated server-side
            // so it needs to be flagged
            // might as well update the partial data, too
            entry.setData({
                ...partial,
                viewedAt: new Date(partial.viewedAt),
                updatedAt: new Date(partial.updatedAt),
                deletedAt: partial.deletedAt ? new Date(partial.deletedAt) : undefined,
                pendingUpdate: true
            })
        }
        return this.saveToIDB(entry)
    }

    /**
     * Fetch an entry by ID.
     *
     * If it doesn't exist in the IDB, a new entry
     * will be created and loaded from the API.
     *
     * If the entry needs an update, it will be
     * refreshed from the API first.
     */
    public async fetch(id: string): Promise<Entry> {
        const entry = await this.fetchFromIDB(id)
        if (entry === undefined) {
            // tried to fetch uncached entry
            // try to pull from API, otherwise throw an error
            try {
                return await this.fetchFromAPI(id)
            } catch (err) {
                throw Error(`Could not find an entry for ID ${id}`)
            }
        }
        if (entry.pendingUpdate && !entry.deleted()) {
            // update the entry
            try {
                return await this.fetchFromAPI(id)
            } catch (err) {
                const { status } = err.response
                if (status === 404) {
                    // existing entry has probably been deleted,
                    // but we haven't yet gotten the update
                    entry.setData({
                        deletedAt: new Date(),
                        pendingUpdate: false
                    })
                    return this.saveToIDB(entry)
                }
                console.log('failed to load entry!!')
                throw err
            }
        }
        return entry
    }

    /**
     * Fetch the entry from IDB
     */
    private async fetchFromIDB(id: string): Promise<Maybe<Entry>> {
        const entry = await DBStore.entries.get(id) as Entry
        return entry && !entry.deleted() ? entry : undefined
    }

    /**
     * Fetch the entry from the API
     */
    private async fetchFromAPI(id: string): Promise<Entry> {
        return this.load(new Entry(id))
    }

    /**
     * Save to the indexed DB
     */
    private async saveToIDB(entry: Entry): Promise<Entry> {
        await DBStore.entries.put(entry.asObject())
        return entry
    }

    /**
     * Save to the API
     */
    private async saveToAPI(entry: Entry): Promise<Entry> {
        const data = entry.synced()
            ? await StoreAPI.update(entry.id!, entry.asObject())
            : await StoreAPI.insert(entry.asObject())

        return entry.setData({
            ...data,
            createdAt: new Date(data.createdAt),
            updatedAt: new Date(data.updatedAt),
            viewedAt: new Date(data.viewedAt),
            pendingUpdate: false
        })
    }

    /**
     * Persist changes in an entry to IDB and API
     */
    public async save(entry: Entry): Promise<Entry> {
        // create new entry
        if (!entry.synced()) {
            // reverse of the normal process -
            // we need to get the entry ID from
            // the API before it's stored into
            // the IDB
            entry = await this.saveToAPI(entry)
            return this.saveToIDB(entry)
        }
        // update existing entry
        await this.saveToIDB(entry)
        return this.saveToAPI(entry)
    }

    /**
     * Load/refresh the entry data from the API
     */
    public async load(entry: Entry): Promise<Entry> {
        if (!entry.synced()) {
            throw Error('Tried to load non-existent entry')
        }
        const data = await StoreAPI.getEntry(entry.id!)
        entry.setData({
            ...data,
            createdAt: new Date(data.createdAt),
            updatedAt: new Date(data.updatedAt),
            viewedAt: new Date(data.viewedAt),
            pendingUpdate: false
        })
        return this.saveToIDB(entry)
    }

    public create(schema: string): Entry {
        if (!stencils[schema]) {
            throw Error(`Unknown entry schema ${schema}`)
        }
        return this.createFromStencil(schema, stencils[schema])
    }

    public createFromStencil(schema: string, stencil: Stencil): Entry {
        return new Entry()
            .setData({
                schemaType: schema,
                icon: stencil.defaultIcon,
                permission: 'creator',
                // generate document key
                documentKey: PCrypto.RSA.encrypt(PCrypto.AES.key()),
                // dummy fields
                title: '',
                sortOrder: 999999,
                tags: [],
                createdBy: PStorage.get(USER_ID),
                createdAt: new Date(),
                updatedAt: new Date(),
                viewedAt: new Date(),
                // do not attempt to update
                pendingUpdate: false
            })
            // copy fields from stencil
            .setFields(
                Object.keys(stencil.fields)
                    .filter(key => key !== 'title')
                    .reduce((fields, fieldKey) => ({
                        ...fields,
                        [fieldKey]: ''
                    }), {})
            )
    }
}

export default new EntryRepository()
